/**
 * The schedule.
 *
 * ---------------------------------------------------------------------
 * A WEEK, LAID OUT AS A WEEK
 *
 * Seven columns, one per day, shifts stacked inside each. Not a table of
 * rows sorted by date, which is what a shift list wants to be and what
 * makes a manager count Tuesday by eye. The question this screen answers
 * is spatial — *where are the holes* — and a list cannot answer a
 * spatial question.
 *
 * On a phone it becomes seven stacked days, because a seven-column grid
 * on a 390px screen is seven unreadable columns.
 *
 * ---------------------------------------------------------------------
 * AN OPEN SHIFT LOOKS DIFFERENT, NOT ABSENT
 *
 * Every unassigned shift is drawn, in its slot, saying it is open. The
 * temptation is to show only what is filled and put "3 open" in a
 * corner; that hides the one thing the manager came here for, and hides
 * it at exactly the size it matters — a Saturday night with two open
 * server shifts is a different evening from one with none.
 *
 * ---------------------------------------------------------------------
 * DRAFT IS VISIBLY DRAFT
 *
 * A published week and a week somebody is still building look nothing
 * alike, because arranging childcare around the wrong one is the whole
 * failure mode. The banner says which, and the publish button says how
 * many shifts it is about to put up and how many of them are still
 * unfilled — publishing with holes is allowed, and somebody should know
 * they are doing it.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date as fmtDate } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Shift {
  id: string;
  work_date: string;
  starts_at: string;
  ends_at: string;
  ends_next_day: boolean;
  scheduled_hours: string;
  job_title: string;
  status: string;
  department_id: string;
  department: string;
  staff_member_id: string | null;
  display_name: string | null;
  is_open: boolean;
  covering_another_department: boolean;
  cancelled_reason: string | null;
  from_a_pattern: boolean;
}

interface Coverage {
  work_date: string;
  department_id: string;
  department: string;
  shifts: string;
  filled: string;
  open: string;
  still_draft: string;
  scheduled_hours: string;
  open_hours: string;
}

interface StaffOption {
  id: string;
  display_name: string;
  department_id: string;
  department: string;
  job_title: string;
}

/** The Monday of the week a date falls in, ISO. */
function mondayOf(d: Date): string {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (x.getUTCDay() + 6) % 7; // 0 = Monday
  x.setUTCDate(x.getUTCDate() - dow);
  return x.toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** 16:00:00 → 4pm. A schedule is read at a glance; seconds are noise. */
function clock(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = (h as number) < 12 ? 'am' : 'pm';
  const hour = (h as number) % 12 === 0 ? 12 : (h as number) % 12;
  return m === 0 ? `${hour}${ampm}` : `${hour}:${String(m).padStart(2, '0')}${ampm}`;
}

export function SchedulePage() {
  const { atLeast } = useSession();
  const isManager = atLeast('manager');

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [department, setDepartment] = useState('');
  // What the last build actually did. useMutation reports busy and the
  // error but not the result, and "nothing to do" and "built 41 shifts"
  // are the two answers worth telling somebody apart.
  const [built, setBuilt] = useState<number | null>(null);
  const weekEnd = addDays(weekStart, 6);

  const params = new URLSearchParams({ from: weekStart, to: weekEnd });
  if (department) params.set('department', department);

  const { data, error, loading, reload } = useApi<{
    shifts: Shift[];
    coverage: Coverage[];
    staff?: StaffOption[];
    may_edit: boolean;
  }>(`/v1/shifts?${params}`, [weekStart, department]);

  const generate = useMutation(async () =>
    api('/v1/shifts/generate', {
      method: 'POST',
      body: { from: weekStart, to: weekEnd, department_id: department || undefined },
    }),
  );

  const publish = useMutation(async () =>
    api('/v1/shifts/publish', {
      method: 'POST',
      body: { from: weekStart, to: weekEnd, department_id: department || undefined },
    }),
  );

  // Not `loading` alone: that unmounts the page on every week change and
  // on the refetch after publishing, which destroys the banner that was
  // about to say what happened.
  if (loading && !data) return <Loading />;

  const shifts = data?.shifts ?? [];
  const coverage = data?.coverage ?? [];
  const departments = Array.from(
    new Map(coverage.map((c) => [c.department_id, c.department])).entries(),
  );

  const live = shifts.filter((s) => s.status !== 'cancelled');
  const open = live.filter((s) => s.is_open).length;
  const draft = live.filter((s) => s.status === 'draft').length;
  const hours = live.reduce((n, s) => n + Number(s.scheduled_hours), 0);
  const openHours = live
    .filter((s) => s.is_open)
    .reduce((n, s) => n + Number(s.scheduled_hours), 0);

  const byDay = DAY_NAMES.map((name, i) => {
    const iso = addDays(weekStart, i);
    return { name, iso, shifts: shifts.filter((s) => s.work_date === iso) };
  });

  return (
    <>
      <h1>Schedule</h1>
      <ErrorBanner error={error ?? generate.error ?? publish.error} />

      <Card
        title={`Week of ${fmtDate(weekStart)}`}
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button
              className="secondary small"
              onClick={() => {
                setBuilt(null);
                setWeekStart(addDays(weekStart, -7));
              }}
            >
              ← Previous
            </button>
            <button className="secondary small" onClick={() => setWeekStart(mondayOf(new Date()))}>
              This week
            </button>
            <button className="secondary small" onClick={() => setWeekStart(addDays(weekStart, 7))}>
              Next →
            </button>
            {departments.length > 1 && (
              <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">Every department</option>
                {departments.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </div>
        }
      >
        <div className="tiles">
          <Tile label="Shifts" value={String(live.length)} />
          <Tile
            label="Still open"
            value={String(open)}
            note={open > 0 ? `${openHours.toFixed(1)} hours nobody's` : 'fully staffed'}
            tone={open > 0 ? 'warning' : 'good'}
          />
          <Tile label="Scheduled hours" value={hours.toFixed(1)} />
          <Tile
            label="Status"
            value={draft > 0 ? 'Draft' : 'Published'}
            note={draft > 0 ? `${draft} not yet up` : 'everyone can see it'}
            tone={draft > 0 ? 'warning' : 'good'}
          />
        </div>

        {isManager && (
          <>
            <div className="actions">
              <button
                className="secondary"
                disabled={generate.busy}
                onClick={async () => {
                  const r = await generate.run();
                  if (r) {
                    setBuilt((r as any).made);
                    reload();
                  }
                }}
              >
                {generate.busy ? 'Building…' : 'Build from patterns'}
              </button>
              <button
                disabled={publish.busy || draft === 0}
                onClick={async () => {
                  const r = await publish.run();
                  if (r) reload();
                }}
              >
                {publish.busy
                  ? 'Publishing…'
                  : draft === 0
                    ? 'Nothing to publish'
                    : `Publish ${draft} shift${draft === 1 ? '' : 's'}`}
              </button>
            </div>
            {built !== null && (
              <p className="muted small">
                {built === 0
                  ? 'Every slot the patterns describe was already there. Nothing was changed — including anything you had assigned or moved.'
                  : `Built ${built} shift${built === 1 ? '' : 's'}. They are drafts until you publish them.`}
              </p>
            )}
            {draft > 0 && open > 0 && (
              <p className="muted small">
                {open} of these are still open. Publishing anyway is fine — a schedule goes up so
                people can see what is free — but they will go up with nobody's name on them.
              </p>
            )}
          </>
        )}
      </Card>

      {live.length === 0 ? (
        <Card title="Nothing Scheduled">
          <Empty>
            {isManager
              ? 'No shifts this week. If you have set up patterns for the ordinary week, "Build from patterns" will lay them out; otherwise add them below.'
              : 'You are not on the schedule this week.'}
          </Empty>
        </Card>
      ) : (
        <div className="week">
          {byDay.map((d) => (
            <section className="week__day" key={d.iso}>
              <header className="week__head">
                <strong>{d.name}</strong>
                <span className="muted small">{fmtDate(d.iso)}</span>
              </header>
              {d.shifts.length === 0 ? (
                <p className="muted small week__none">—</p>
              ) : (
                d.shifts.map((s) => (
                  <ShiftBlock
                    key={s.id}
                    shift={s}
                    staff={data?.staff ?? []}
                    mayEdit={data?.may_edit ?? false}
                    onChanged={reload}
                  />
                ))
              )}
            </section>
          ))}
        </div>
      )}

      {isManager && <Patterns onChanged={reload} departments={departments} />}
    </>
  );
}

function ShiftBlock({
  shift,
  staff,
  mayEdit,
  onChanged,
}: {
  shift: Shift;
  staff: StaffOption[];
  mayEdit: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);

  const assign = useMutation(async (staffId: string | null) =>
    api(`/v1/shifts/${shift.id}`, { method: 'PATCH', body: { staff_member_id: staffId } }),
  );

  const drop = useMutation(async (reason: string) =>
    api(`/v1/shifts/${shift.id}?reason=${encodeURIComponent(reason)}`, { method: 'DELETE' }),
  );

  const cls = [
    'shift',
    shift.status === 'cancelled' ? 'shift--cancelled' : '',
    shift.status === 'draft' ? 'shift--draft' : '',
    shift.is_open && shift.status !== 'cancelled' ? 'shift--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls}>
      <div className="shift__time">
        {clock(shift.starts_at)}–{clock(shift.ends_at)}
        {shift.ends_next_day && <span className="muted small"> next day</span>}
      </div>
      <div className="shift__title">{shift.job_title}</div>
      <div className="shift__who">
        {shift.status === 'cancelled' ? (
          <span className="muted small">Cancelled — {shift.cancelled_reason}</span>
        ) : shift.is_open ? (
          <Status tone="warning">Open</Status>
        ) : (
          <>
            {shift.display_name}
            {shift.covering_another_department && (
              <span className="muted small"> · covering</span>
            )}
          </>
        )}
      </div>
      <div className="muted small">
        {shift.department} · {shift.scheduled_hours}h
        {shift.status === 'draft' && ' · draft'}
      </div>

      {mayEdit && shift.status !== 'cancelled' && (
        <>
          <button className="secondary small" onClick={() => setOpen((o) => !o)}>
            {open ? 'Close' : shift.is_open ? 'Fill' : 'Change'}
          </button>
          {open && (
            <div>
              <ErrorBanner error={assign.error ?? drop.error} />
              <select
                value={shift.staff_member_id ?? ''}
                disabled={assign.busy}
                onChange={async (e) => {
                  const r = await assign.run(e.target.value || null);
                  if (r) onChanged();
                }}
              >
                <option value="">Leave it open</option>
                {/* Own department first: it is who normally does this, and
                    a list of the whole club makes finding them work. */}
                <optgroup label={shift.department}>
                  {staff
                    .filter((s) => s.department_id === shift.department_id)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.display_name} — {s.job_title}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Covering from elsewhere">
                  {staff
                    .filter((s) => s.department_id !== shift.department_id)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.display_name} — {s.department}
                      </option>
                    ))}
                </optgroup>
              </select>
              <button
                className="danger small"
                disabled={drop.busy}
                onClick={async () => {
                  const r = await drop.run(
                    shift.status === 'published' ? 'Taken off the schedule' : 'Removed',
                  );
                  if (r) onChanged();
                }}
              >
                {drop.busy
                  ? 'Removing…'
                  : shift.status === 'published'
                    ? 'Cancel it'
                    : 'Remove'}
              </button>
              {shift.status === 'published' && (
                <p className="muted small" style={{ marginBottom: 0 }}>
                  It stays on the sheet, marked cancelled — somebody may already have arranged
                  their week around it.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The ordinary week.
 *
 * Kept below the schedule rather than on its own screen: a manager
 * discovers they need a pattern while looking at a week that is missing
 * one, and a pattern edited two clicks away from the thing it produces
 * is a pattern that gets edited.
 */
function Patterns({
  departments,
  onChanged,
}: {
  departments: [string, string][];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { data, error, reload } = useApi<{ patterns: any[] }>(
    open ? '/v1/shifts/patterns' : null,
    [open],
  );
  const [form, setForm] = useState({
    department_id: '',
    weekday: '6',
    job_title: '',
    starts_at: '09:00',
    ends_at: '17:00',
    headcount: '1',
  });

  const create = useMutation(async () => {
    const crosses = form.ends_at <= form.starts_at;
    return api('/v1/shifts/patterns', {
      method: 'POST',
      body: {
        department_id: form.department_id,
        weekday: Number(form.weekday),
        job_title: form.job_title.trim(),
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        // Inferred here rather than asked, because the answer is always
        // yes when the end is earlier than the start and the question
        // would be one more thing to get wrong. The database refuses the
        // combination that disagrees.
        ends_next_day: crosses,
        headcount: Number(form.headcount),
      },
    });
  });

  const retire = useMutation(async (id: string, active: boolean) =>
    api(`/v1/shifts/patterns/${id}`, { method: 'PATCH', body: { is_active: active } }),
  );

  return (
    <Card
      title="The Ordinary Week"
      actions={
        <button className="secondary small" onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : 'Set it up'}
        </button>
      }
    >
      {!open ? (
        <p className="muted small" style={{ marginBottom: 0 }}>
          The shifts a normal week has — Saturday needs three servers, the grounds crew start at
          half past five. "Build from patterns" lays them onto real dates, and never touches a day
          you have already changed.
        </p>
      ) : (
        <>
          <ErrorBanner error={error ?? create.error ?? retire.error} />

          {(data?.patterns ?? []).length === 0 ? (
            <Empty>No patterns yet. Add the first one below.</Empty>
          ) : (
            <div className="table">
              <table className="data data--cards">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Department</th>
                    <th>Job</th>
                    <th>Hours</th>
                    <th className="num">People</th>
                    <th>Season</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data?.patterns ?? []).map((p: any) => (
                    <tr key={p.id} className={p.is_active ? undefined : 'muted'}>
                      <td data-label="Day" className="cell--title">
                        {DAY_NAMES[p.weekday - 1]}
                      </td>
                      <td data-label="Department" className="muted cell--aside">
                        {p.department}
                      </td>
                      <td data-label="Job">{p.job_title}</td>
                      <td data-label="Hours">
                        {clock(p.starts_at)}–{clock(p.ends_at)}
                        {p.ends_next_day && <span className="muted small"> next day</span>}
                      </td>
                      <td data-label="People" className="num">
                        {p.headcount}
                      </td>
                      <td data-label="Season" className="muted cell--aside">
                        {p.effective_from || p.effective_to
                          ? `${p.effective_from ? fmtDate(p.effective_from) : 'always'} – ${
                              p.effective_to ? fmtDate(p.effective_to) : 'always'
                            }`
                          : 'all year'}
                      </td>
                      <td data-label="">
                        <button
                          className="secondary small"
                          disabled={retire.busy}
                          onClick={async () => {
                            const r = await retire.run(p.id, !p.is_active);
                            if (r) {
                              reload();
                              onChanged();
                            }
                          }}
                        >
                          {p.is_active ? 'Stop using' : 'Use again'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid-2">
            <label className="field">
              <span>Department</span>
              <select
                value={form.department_id}
                onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
              >
                <option value="">Choose one</option>
                {departments.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Day</span>
              <select
                value={form.weekday}
                onChange={(e) => setForm((f) => ({ ...f, weekday: e.target.value }))}
              >
                {DAY_NAMES.map((n, i) => (
                  <option key={n} value={String(i + 1)}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Job</span>
              <input
                value={form.job_title}
                onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                placeholder="Server"
              />
            </label>
            <label className="field">
              <span>How many people</span>
              <input
                type="number"
                min={1}
                max={60}
                value={form.headcount}
                onChange={(e) => setForm((f) => ({ ...f, headcount: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Starts</span>
              <input
                type="time"
                value={form.starts_at}
                onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Ends</span>
              <input
                type="time"
                value={form.ends_at}
                onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
              />
              {form.ends_at <= form.starts_at && (
                <div className="hint">Ends the following morning.</div>
              )}
            </label>
          </div>
          <div className="actions">
            <button
              disabled={create.busy || !form.department_id || form.job_title.trim() === ''}
              onClick={async () => {
                const r = await create.run();
                if (r) {
                  setForm((f) => ({ ...f, job_title: '' }));
                  reload();
                }
              }}
            >
              {create.busy ? 'Adding…' : 'Add'}
            </button>
          </div>
          <p className="muted small" style={{ marginBottom: 0 }}>
            A pattern you stop using leaves the shifts it already made alone. Building again simply
            stops producing new ones.
          </p>
        </>
      )}
    </Card>
  );
}
